import {
  resolveActionLayoutLevelText,
  resolveActionLayoutPositionText,
  resolveActionLayoutTextId,
} from '@/services/action/actionScriptRuntime'

export type ElementLayoutActionFilter = {
  targetElementId: string
  level?: string
  positions: string[]
}

export function getActionDropDownName(action: Record<string, unknown>): string {
  const dropDown = (action as { dropDownActionName?: unknown }).dropDownActionName
  if (dropDown == null) return ''
  if (Array.isArray(dropDown)) {
    return String((dropDown[0] as { _text?: string })?._text ?? '').trim()
  }
  if (typeof dropDown === 'object' && '_text' in dropDown) {
    return String((dropDown as { _text?: string })._text ?? '').trim()
  }
  if (typeof dropDown === 'string') {
    return dropDown.trim()
  }
  return ''
}

export function isActionDropDownMain(action: Record<string, unknown>): boolean {
  const dropDown = (action as { dropDownMain?: unknown }).dropDownMain
  if (dropDown == null) return false
  let value: unknown
  if (Array.isArray(dropDown)) {
    value = (dropDown[0] as { _text?: unknown })?._text
  } else if (typeof dropDown === 'object' && '_text' in dropDown) {
    value = (dropDown as { _text?: unknown })._text
  } else {
    value = dropDown
  }
  return value === 'true' || value === true
}

export function isDropdownChildActionDefinition(action: unknown): boolean {
  if (action == null || typeof action !== 'object') return false
  const record = action as Record<string, unknown>
  const dropDownName = getActionDropDownName(record)
  return dropDownName !== '' && !isActionDropDownMain(record)
}

/** layout.level=element + layout.position=inline — ElementRenderer, не MAction/getActions(). */
export function isInlineLayoutActionDefinition(action: unknown): boolean {
  if (!action || typeof action !== 'object') return false
  const layout = (action as { layout?: unknown }).layout
  return (
    resolveActionLayoutLevelText(layout) === 'element' &&
    resolveActionLayoutPositionText(layout) === 'inline'
  )
}

/** ACTION-states: inline/events не в getActions(); dropdown — через useDropdownGroupActionRegistry. */
export function isNonRegistryLayoutActionDefinition(action: unknown): boolean {
  if (!action || typeof action !== 'object') return false
  if (isInlineLayoutActionDefinition(action)) return true
  const layout = (action as { layout?: unknown }).layout
  return resolveActionLayoutPositionText(layout) === 'events'
}

/** Фильтр layout-actions элемента (контракт ActionsRow: level + id + position + dropdown). */
export function filterElementLayoutActions(
  pageActions: Record<string, unknown>[],
  filter: ElementLayoutActionFilter,
): Record<string, unknown>[] {
  const targetId = filter.targetElementId
  const targetLevel = filter.level ?? 'element'

  return pageActions.filter((action) => {
    const levelMatch = resolveActionLayoutLevelText(action.layout) === targetLevel
    const idMatch = resolveActionLayoutTextId(action.layout) === targetId
    const actionPosition = resolveActionLayoutPositionText(action.layout)
    const positionMatch = filter.positions.includes(actionPosition)

    const dropDownName = getActionDropDownName(action)
    if (dropDownName && !isActionDropDownMain(action)) {
      return false
    }

    return levelMatch && idMatch && positionMatch
  })
}

export function sortActionsByLayoutSort(actions: Record<string, unknown>[]): Record<string, unknown>[] {
  return [...actions].sort((a, b) => {
    const sortA = parseInt(String((a._attributes as { sort?: string })?.sort ?? ''), 10) || 0
    const sortB = parseInt(String((b._attributes as { sort?: string })?.sort ?? ''), 10) || 0
    return sortA - sortB
  })
}

export function resolveFirstElementLayoutAction(
  pageActions: Record<string, unknown>[],
  filter: ElementLayoutActionFilter,
): Record<string, unknown> | null {
  const sorted = sortActionsByLayoutSort(filterElementLayoutActions(pageActions, filter))
  return sorted[0] ?? null
}

/** Element-level layout actions follow the target widget shell; missing flag stays visible. */
export function isElementLayoutActionTargetVisible(
  targetElementId: string | number | undefined,
  shellVisibleByElementId?: Record<string, boolean> | null,
): boolean {
  const id = String(targetElementId ?? '').trim()
  if (!id) return true
  return shellVisibleByElementId?.[id] !== false
}
